// useLayoutKeyboardShortcuts — レイアウト非依存のキーボードショートカット配線フック。
//
// 由来 (2026-06-11): Classic (ClassicLayout) に document/window-level の keydown
// listener として直書きされていた以下のショートカット群を **共有カスタムフック**
// に逐語抽出し、Modern にも同じ流儀で配線できるようにする (= `useSpaceHoldPan` と
// 同じ階層・同じ「アクションを callback で受け取り layout 非依存にする」流儀)。
//
//   - Ctrl/Cmd+Enter → 発言小窓を開く (chord, generic 経路、ClassicLayout @~711)。
//   - V              → キャンバス左右反転トグル (素キー, generic 経路、@~754)。
//   - Tab            → UI 表示/非表示トグル (@~986)。
//   - B              → ブラシ選択 (@~1047)。
//   - S              → サインペン (`onSelectPen('flat')`) 直接選択。
//   - E              → 消しゴム直接選択 (@~1059)。
//   - `+`/`=`        → ズーム in (@~1064)、`-` → ズーム out (@~1071)、`0` → ズームリセット (@~1075)。
//
// Space hold pan (= `useSpaceHoldPan`) は別フックで既に Classic/Modern 両方に配線
// 済みなので本フックの対象外 (= 二重 listen を避ける)。
//
// ## 設計方針
//
// - **アクションは callback で受け取る** (= layout 非依存)。callback が `undefined`
//   の shortcut は no-op にして「その layout が持たない機能」を安全に省略できる。
//   (Classic/Modern とも Tab = UI 隠しを配線するが、Modern は両サイドバーのみ畳む
//   = 2026-06-12「Modern Tab 決着」。callback の中身が layout で異なるだけ。)
// - **判定・ガードは Classic の現挙動を完全踏襲**する:
//     - Ctrl/Cmd+Enter は chord (`Ctrl+Enter` / `Meta+Enter`) として document 発火 +
//       `!e.isComposing` IME ガード + `preventDefault` (= 旧 `isOpenPopupHotkey` と等価)。
//     - V は素キーとして window 発火 + `shouldSkipBareKey` (Ctrl/Meta/Alt/Shift 併用無効・
//       入力欄フォーカス無効) + `!e.isComposing` IME ガード (= 旧 `isCanvasFlipHotkey` と
//       等価)。素の V は browser 既定動作が無いので `preventDefault` は呼ばない。
//     - Tab / B,S,E / zoom は「入力欄 (INPUT/TEXTAREA/SELECT/contenteditable)
//       フォーカス中はスルー」「修飾キー併用はスルー (素キーのみ)」+ `preventDefault`。
// - **IME ガードは generic 経路の chord/bare 両パスに共通で効かせる** (= 全 action 安全
//   強化)。旧 guard 特例 (`isOpenPopupHotkey` / `isCanvasFlipHotkey`) は撤去。
// - **listener attach 先 / preventDefault の有無は Classic 原典に合わせる**:
//     - Ctrl/Cmd+Enter は chord なので `document` に attach (= canvas / chat input
//       どこにフォーカスがあっても効く)。
//     - V / Tab / B,S,E / zoom は素キーなので `window` に attach。
// - **stale closure 対策**: 全 callback を 1 つの ref にまとめて毎 render 更新し、
//   listener 内では ref 経由で最新を読む (= deps 空 useEffect でも常に最新を呼ぶ。
//   Classic の `handleToggleFlipRef` 流儀を全 callback に一般化)。

import type { StrokePen } from '@oekaki/protocol';
import { useEffect, useRef } from 'react';

import type { ShortcutOverrides } from '../../infrastructure/shortcutOverridesStore.js';
import { resolveShortcutCycle } from './shortcutCycle.js';
import { useShortcutOverrides } from './shortcutOverridesContext.js';
import {
  chordHasModifier,
  eventKeyOf,
  formatChordFromEvent,
  matchChordEvent,
  parseChord,
  resolveEffectiveKeys,
  resolvePreventDefault,
  SHORTCUT_REGISTRY,
  type ShortcutAction,
  type ShortcutCallbackKey,
} from './shortcutRegistry.js';
import { isTextInputFocus } from './useSpaceHoldPan.js';

/**
 * レイアウトキーボードショートカットのアクション群。各 callback は省略可
 * (= `undefined` のとき対応する shortcut は no-op)。layout が持たない機能を
 * 安全に省ける。
 */
export interface LayoutKeyboardShortcutActions {
  /** Ctrl/Cmd+Enter: 発言小窓 (floating chat popup) を開く。 */
  readonly onOpenChatPopup?: () => void;
  /** Tab: UI 表示/非表示トグル (= canvas 集中モード)。 */
  readonly onToggleUiHidden?: () => void;
  /** B: ブラシ選択。 */
  readonly onSelectBrush?: () => void;
  /** S: サインペン (`onSelectPen('flat')`) 直接選択。 */
  readonly onSelectSignpen?: () => void;
  /** E: 消しゴム直接選択。 */
  readonly onSelectEraserDirect?: () => void;
  /** I: スポイト持続モードへ (Modern 専用、§9.6)。Classic は未渡し = no-op。 */
  readonly onSelectEyedropper?: () => void;
  // ── CSP流 循環ショートカット (shortcut-settings.md「CSP の実モデル」2026-08-21) ──
  // 個別ペンの直接選択。既定キー未割当 (リバインドで初めて効く)。brush/flat/eraser は
  // 上の既存 3 つを使う (registry の `penRef` で循環アルゴリズムに参加する点は同じ)。
  /** ドット直接選択。 */
  readonly onSelectDot?: () => void;
  /** トーン直接選択。 */
  readonly onSelectTone?: () => void;
  /** エアブラシ直接選択。 */
  readonly onSelectAirbrush?: () => void;
  /** クレヨン直接選択。 */
  readonly onSelectCrayon?: () => void;
  /** パステル直接選択。 */
  readonly onSelectPastel?: () => void;
  /** テクスチャ直接選択。 */
  readonly onSelectTexturePen?: () => void;
  /** 「線画」タブの最後に使ったペンへ。 */
  readonly onSelectTabLine?: () => void;
  /** 「塗り」タブの最後に使ったペンへ。 */
  readonly onSelectTabFill?: () => void;
  /** 「質感」タブの最後に使ったペンへ。 */
  readonly onSelectTabTexture?: () => void;
  /** `+`/`=`: ズーム in。 */
  readonly onZoomIn?: () => void;
  /** `-`: ズーム out。 */
  readonly onZoomOut?: () => void;
  /** `0`: ズームリセット。 */
  readonly onZoomReset?: () => void;
  /**
   * `f`: 全体表示 (フィット、決定3 §6 consult 2026-09-29)。canvas 全景が表示
   * スロットに収まる zoom へ戻す (`chrome.applyFitZoom`)。Classic/Modern 両方に
   * 配線 (ボタンは desktop Modern のみ、Classic はショートカットで到達)。
   */
  readonly onZoomFit?: () => void;
  /** V: キャンバス左右反転トグル。 */
  readonly onToggleFlip?: () => void;
  /** R: 回転ツール (持続ツール、canvas-rotation 段取り 2)。 */
  readonly onSelectRotateTool?: () => void;
  /** 左回転 1 ステップ (既定キー無し)。 */
  readonly onRotateLeft?: () => void;
  /** 右回転 1 ステップ (既定キー無し)。 */
  readonly onRotateRight?: () => void;
  /** 回転を 0° に戻す (既定キー無し)。 */
  readonly onRotateReset?: () => void;
  /** `?`: ショートカット設定モーダルを開く。 */
  readonly onOpenShortcutSettings?: () => void;
  /** `` ` ``: ワークスペース preset を巡回 (Modern 専用、§9.10 決定4)。Classic は no-op。 */
  readonly onCyclePreset?: () => void;
  /**
   * Ctrl/Cmd+Z: conflict-aware Undo (Modern 専用、undo-conflict-aware-2026-07-12.md)。
   * `edit.undo` の `guardTextInput` によりチャット入力欄フォーカス中は発火しない
   * (= ブラウザ既定のテキスト編集 Undo を奪わない)。Classic は未渡し = no-op。
   */
  readonly onUndo?: () => void;
  /**
   * `[`: 太さプリセット段階減 (modern-layout.md §9.11、consult 2026-09-29、Modern 専用)。
   * Classic は未渡し = no-op (6 cell 上書き型のまま、§9.11 決定4)。
   */
  readonly onWidthStepDown?: () => void;
  /** `]`: 太さプリセット段階増 (上と対称)。 */
  readonly onWidthStepUp?: () => void;
}

/**
 * CSP流 循環ショートカットの解決に必要な session-scoped 状態 (shortcut-settings.md
 * 「CSP の実モデル」2026-08-21 確定)。**`chrome` (`useRoomChrome`) が所有**する —
 * Classic⇄Modern のレイアウト切替で `useLayoutKeyboardShortcuts` 自体は remount
 * される (`App.tsx` の `chromeView` 分岐、chrome より下位) ため、本 hook のローカル
 * state に持たせると切替のたびに循環位置が消えてしまう。「退室・入室時のみリセット」
 * という仕様 (= `chrome` と同じ room session 寿命) を満たすには、chrome 側の state を
 * ここへ**渡してもらう**必要がある。
 */
export interface ShortcutCycleState {
  /** 現在選択中の pen (eraser 含む、`resolveShortcutCycle` の現在位置判定に使う)。 */
  readonly currentPen: StrokePen;
  /**
   * binding (= `formatChordFromEvent` で正規化したキー文字列) ごとに記憶した前回の
   * 解決 index。複数候補 (循環) が実際に発生したキーだけが entry を持つ (= 既定
   * キーマップでは常に空、`chrome` の初期値もそのまま `{}` でよい)。
   */
  readonly rememberedIndexByBinding: Readonly<Record<string, number>>;
  /** `rememberedIndexByBinding` の更新 (= chrome の room-session state へ書き戻す)。 */
  readonly onRememberedIndexChange: (binding: string, index: number) => void;
}

/**
 * 1 つのキー押下に複数 candidate が割り当たっているとき (= CSP流循環) の勝者を解決する。
 * `candidates.length === 1` (既定キーマップの通常ケース) は `resolveShortcutCycle` を
 * 経由しても必ずその 1 件を返す (= 既定挙動を壊さない、shortcutCycle.ts 参照)。
 */
const resolveWinner = (
  candidates: readonly ShortcutAction[],
  bindingKey: string,
  cycle: ShortcutCycleState,
): { readonly action: ShortcutAction; readonly nextRememberedIndex: number } =>
  resolveShortcutCycle(candidates, {
    currentPen: cycle.currentPen,
    rememberedIndex: cycle.rememberedIndexByBinding[bindingKey] ?? null,
  });

/**
 * 素キーショートカット (Tab / B,S,E / zoom) の「スルー」判定。Classic 原典の
 * 早期 return を逐語踏襲: 修飾キー併用 (Ctrl/Meta/Alt/Shift) はスルー、入力欄
 * (INPUT/TEXTAREA/SELECT/contenteditable) フォーカス中はスルー。
 *
 * `true` のとき呼び出し側は当該キーを **処理せず return** する (= 通常動作を尊重)。
 * 純関数として切り出し、DOM 不要のロジックテストを可能にする。
 *
 * `allowShift` (= 既定 false): `?` (= Shift+/) のように **生成に Shift が要る bare key**
 * 向けに Shift だけ許可する (= chat 入力欄ガードは効かせつつ Shift を除外しない)。
 * Ctrl/Meta/Alt は引き続き除外。
 */
export const shouldSkipBareKey = (e: KeyboardEvent, allowShift = false): boolean => {
  if (e.ctrlKey || e.metaKey || e.altKey) return true;
  if (e.shiftKey && !allowShift) return true;
  return isTextInputFocus(e.target);
};

/**
 * 動的 attach 導出 (= スライス2 申し送り、スライス3 で guard 特例を撤去)。実効 binding を
 * action 単位で「bare (修飾無し) → window」「chord (修飾あり) → document」に振り分ける。
 *
 * 全 action を override 込みの effective binding で 1 つずつ見て、`chordHasModifier` で
 * window/document へ仕分ける (= リバインドで素キー↔chord が変わっても attach 先が追従)。
 * 旧 guard 固定 (`chat.openPopup` / `view.flip`) もこの generic 経路に合流した。
 */
interface AttachedBinding {
  readonly action: ShortcutAction;
  /** この binding を発火させる chord 文字列。 */
  readonly binding: string;
}
interface AttachPlan {
  readonly windowBindings: readonly AttachedBinding[];
  readonly documentBindings: readonly AttachedBinding[];
}

const buildAttachPlan = (overrides: ShortcutOverrides): AttachPlan => {
  const windowBindings: AttachedBinding[] = [];
  const documentBindings: AttachedBinding[] = [];
  for (const action of SHORTCUT_REGISTRY) {
    for (const binding of resolveEffectiveKeys(action, overrides)) {
      const parsed = parseChord(binding);
      if (parsed.key === '') continue;
      if (chordHasModifier(parsed)) {
        documentBindings.push({ action, binding });
      } else {
        windowBindings.push({ action, binding });
      }
    }
  }
  return { windowBindings, documentBindings };
};

/** action の callback を解決して呼ぶ (preventDefault は呼び出し側が済ませる)。 */
const fireCallback = (action: ShortcutAction, actions: LayoutKeyboardShortcutActions): void => {
  const cb = actions[action.callbackKey as ShortcutCallbackKey];
  cb?.();
};

/**
 * Classic のキーボードショートカット listener 群を 1 本化した共有フック。
 * callback を ref 経由で最新参照し、override を `useShortcutOverrides` で購読して
 * 変更時に attach plan を作り直す (= リバインドが即時反映)。
 *
 * 2026-06-12 (スライス2/3): 静的 `DOCUMENT_ACTIONS`/`WINDOW_ACTIONS` 分割を廃止し、
 * **effective binding から binding 単位で attach を動的導出** (`buildAttachPlan`)。
 * 全 action を素キー → window + `shouldSkipBareKey`、chord → document に振り分ける
 * (= 旧 guard 固定 Ctrl+Enter / V もこの generic 経路に合流、スライス3)。
 * chord/bare 両パスに `!e.isComposing` の IME ガードを効かせる。preventDefault は
 * registry 宣言 (`resolvePreventDefault`) に従う。
 *
 * 2026-08-21 (CSP流 循環ショートカット): 1 回の keydown で **同じ binding にマッチする
 * candidate を全部集めてから** `resolveWinner`/`resolveShortcutCycle` で 1 件を選ぶ
 * (= 旧「最初にマッチした 1 件で即 return」から変更)。`candidates.length === 1`
 * (既定キーマップの通常ケース) は必ずその 1 件が選ばれるので挙動は不変。複数候補が
 * 実際に発生したときだけ `cycle.onRememberedIndexChange` で位置を記憶する (= 既定
 * キーマップでは 1 度も呼ばれない、余計な再 render を増やさない)。
 */
export const useLayoutKeyboardShortcuts = (
  actions: LayoutKeyboardShortcutActions,
  cycle: ShortcutCycleState,
): void => {
  // 全 callback を 1 ref に集約して毎 render 更新する (= listener から常に最新を読む)。
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // 循環解決に要る session 状態も同様に ref 経由で最新参照する (= stale closure 回避)。
  const cycleRef = useRef(cycle);
  cycleRef.current = cycle;

  // override を購読 (= リバインド確定で再 render → 下の useEffect が plan を作り直す)。
  const overrides = useShortcutOverrides();

  // ── document-level listener ────────────────────────────────────────────────
  // chord binding (= 修飾キー付き、Ctrl/Cmd+Enter 含む) を処理する。canvas / chat
  // input どこにフォーカスがあっても効く (Classic 原典踏襲。chord は修飾キー入りで
  // 誤発火しにくく、入力欄でも明示的な意図とみなして発火させる = D2 / 旧 isOpenPopupHotkey
  // と同思想)。IME 変換中 (`e.isComposing`) は発火しない (= 旧 isOpenPopupHotkey の IME
  // ガードを generic 経路に一般化)。
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const plan = buildAttachPlan(overrides);
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.isComposing) return;
      const candidates: ShortcutAction[] = [];
      for (const { action, binding } of plan.documentBindings) {
        if (!matchChordEvent(binding, e)) continue;
        // `edit.undo` 用の例外ガード (guardTextInput): 入力欄フォーカス中は素通し
        // し、ブラウザ既定のテキスト編集 Undo を奪わない (他の chord action は
        // 従来通り入力欄でも発火する、旧 isOpenPopupHotkey と同じ挙動)。
        if (action.guardTextInput === true && isTextInputFocus(e.target)) continue;
        candidates.push(action);
      }
      if (candidates.length === 0) return;
      const bindingKey = formatChordFromEvent(e);
      if (bindingKey === null) return;
      const { action: winner, nextRememberedIndex } = resolveWinner(
        candidates,
        bindingKey,
        cycleRef.current,
      );
      if (resolvePreventDefault(winner)) e.preventDefault();
      fireCallback(winner, actionsRef.current);
      if (candidates.length > 1) {
        cycleRef.current.onRememberedIndexChange(bindingKey, nextRememberedIndex);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [overrides]);

  // ── window-level listener ──────────────────────────────────────────────────
  // bare binding (= 素キー、V 含む) を処理する。action ごとに shouldSkipBareKey を評価する
  // (= `?` だけ allowShift で Shift を許す)。それ以外は修飾キー無し + 非入力欄のみ。IME
  // 変換中 (`e.isComposing`) は発火しない (= 旧 isCanvasFlipHotkey の IME ガードを一般化)。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const plan = buildAttachPlan(overrides);
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.isComposing) return;
      // WEB-7: `key` を持たないイベントが外部から飛んでくる (詳細は `eventKeyOf`)。
      // ここで弾かないと keydown のたびに throw し、ショートカットが全滅する。
      const eventKey = eventKeyOf(e);
      if (eventKey === null) return;
      const candidates: ShortcutAction[] = [];
      for (const { action, binding } of plan.windowBindings) {
        if (shouldSkipBareKey(e, action.allowShift ?? false)) continue;
        // bare binding は base key を大小無視照合 (旧 `case 'p':/'P':` の一般化)。
        if (eventKey.toLowerCase() !== parseChord(binding).key.toLowerCase()) continue;
        candidates.push(action);
      }
      if (candidates.length === 0) return;
      const bindingKey = formatChordFromEvent(e);
      if (bindingKey === null) return;
      const { action: winner, nextRememberedIndex } = resolveWinner(
        candidates,
        bindingKey,
        cycleRef.current,
      );
      if (resolvePreventDefault(winner)) e.preventDefault();
      fireCallback(winner, actionsRef.current);
      if (candidates.length > 1) {
        cycleRef.current.onRememberedIndexChange(bindingKey, nextRememberedIndex);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [overrides]);
};
